import { Box } from '@mui/material';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from '@tanstack/react-router';

import { Icon, Row, MenuButton } from '@/components/ui';
import { useFooterContext } from './FooterContext';
import { useProjectsStore } from '@/features/projects/store';

// Import menu icons
import homeMenu from '@/assets/home-menu.svg';
import homeActiveMenu from '@/assets/home-active-menu.svg';
import sitesMenu from '@/assets/sites-menu.svg';
import sitesActiveMenu from '@/assets/sites-active-menu.svg';
import materialMenu from '@/assets/material-menu.svg';
import materialActiveMenu from '@/assets/material-active-menu.svg';
import settingsMenu from '@/assets/settings-menu.svg';
import settingsActiveMenu from '@/assets/settings-active-menu.svg';
import plusWhiteIcon from '@/assets/plus-white.svg';

export const Footer: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { showPlus, plusClickHandler } = useFooterContext();
  const { currentProject } = useProjectsStore(state => state);

  if (location.pathname === '/auth/login') {
    return null;
  }

  const handleNavigate = (to: string) => {
    navigate({ to });
  };

  const handleSitesClick = () => {
    if (!currentProject?.id) {
      navigate({ to: '/projects' });
    } else {
      navigate({ to: `/projects/${currentProject.id}/sites` });
    }
  };

  return (
    <>
      <Box sx={{ height: '4.0625rem' }} />
      <Box
        zIndex={9999}
        sx={{
          position: 'fixed',
          bottom: '0',
          left: 0,
          width: '100%',
          height: '4.0625rem', // 65px
          zIndex: 100,
        }}
      >
        {showPlus ? (
          <>
            {/* Combined background with shadow (with notch) */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                height: '100%',
                filter: 'drop-shadow(0px 4px 12px rgba(63, 136, 227, 0.5))',
              }}
            >
              {/* White background bar */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'white',
                  borderTopLeftRadius: '1.375rem', // 32px
                  borderTopRightRadius: '1.375rem', // 32px
                }}
              />

              {/* SVG Notch overlay - centered */}
              <Box
                component='svg'
                viewBox='0 0 197 32'
                preserveAspectRatio='xMidYMin meet'
                sx={{
                  position: 'absolute',
                  top: '-1.38rem',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '12.3125rem', // 197px
                  height: '2rem', // 32px
                }}
              >
                <path
                  d='M98.5 0C112.646 0 124.885 5.1 130.77 14C133.94 18.6 139.679 22 146.816 22H197V32H0V22H50.184C57.321 22 63.06 18.6 66.23 14C72.115 5.1 84.354 0 98.5 0Z'
                  fill='white'
                />
              </Box>
            </Box>

            {/* Circular FAB Button */}
            <Box
              onClick={plusClickHandler}
              sx={{
                position: 'absolute',
                top: '-0.7rem',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '3.4375rem', // 55px
                height: '3.4375rem', // 55px
                background: '#1C5B9C',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0px 4px 12px rgba(63, 136, 227, 0.5)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                zIndex: 2,
                '&:active': {
                  transform: 'translateX(-50%) scale(0.95)',
                  boxShadow: '0px 2px 8px rgba(63, 136, 227, 0.4)',
                },
              }}
            >
              <Icon
                src={plusWhiteIcon}
                sx={{ width: '1.5rem', height: '1.5rem' }}
              />
            </Box>
          </>
        ) : (
          /* Flat background bar (without notch) */
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'white',
              boxShadow: '0px -2px 8px rgba(63, 136, 227, 0.2)',
              borderTopLeftRadius: '2rem', // 32px
              borderTopRightRadius: '2rem', // 32px
            }}
          />
        )}

        {/* Navigation buttons */}
        <Row
          justifyContent='space-around'
          sx={{
            px: { xs: 1, sm: 1 },
            py: 1,
            maxWidth: { xs: '100%', sm: 900 },
            margin: 'auto',
            height: '100%',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <MenuButton
            handleClick={() => handleNavigate('/projects')}
            iconSrc={homeMenu}
            activeIcon={homeActiveMenu}
            active={location.pathname === '/projects'}
            text={t('home_menu_button')}
          />

          <MenuButton
            handleClick={handleSitesClick}
            iconSrc={sitesMenu}
            activeIcon={sitesActiveMenu}
            active={location.pathname.includes('sites')}
            text={t('sites_menu_button')}
            iconMargin='-0.125rem 0 0 0' // -2px
          />

          {/* Empty space for FAB - only when FAB is visible */}
          {showPlus && <Box sx={{ minWidth: '3.4375rem' }} />}

          <MenuButton
            handleClick={() => handleNavigate('/materials')}
            iconSrc={materialMenu}
            activeIcon={materialActiveMenu}
            active={location.pathname === '/materials'}
            text={t('material_menu_button')}
            iconMargin='-0.1875rem 0 0 0' // -3px
          />

          <MenuButton
            handleClick={() => handleNavigate('/settings')}
            iconSrc={settingsMenu}
            activeIcon={settingsActiveMenu}
            active={location.pathname === '/settings'}
            text={t('settings_menu_button')}
          />
        </Row>
      </Box>
    </>
  );
};
